/* global google */
import i18n from "i18next";
import Colors from "../themes/Colors";
import { VENUE_CATEGORIES, OTHER_VENUE_CATEGORY } from "../constants/venueCategories";

// Legend entries: every configured category plus "Others" last.
export const VENUE_CATEGORY_LIST = [...VENUE_CATEGORIES, OTHER_VENUE_CATEGORY];

// OpenOOH root id -> category, from each category's rootIds.
const CATEGORY_BY_ROOT = {};
VENUE_CATEGORIES.forEach((category) =>
  category.rootIds.forEach((rootId) => {
    CATEGORY_BY_ROOT[rootId] = category;
  })
);

// Root-to-leaf OpenOOH ids of a display, e.g. [1, 106, 10602]. Uses the backend
// path when present; otherwise derives it from the id, since each OpenOOH level
// appends two digits (10602 -> 106 -> 1). Covers endpoints that don't send the path.
export const getVenuePath = (display) => {
  const path = display && display.open_ooh_venue_type_path;
  if (Array.isArray(path) && path.length) return path;
  const id = Number(display && display.open_ooh_venue_type_id);
  const derived = [];
  for (let s = id > 0 ? String(Math.trunc(id)) : ""; s; s = s.slice(0, -2)) {
    derived.unshift(Number(s));
  }
  return derived;
};

// Category (color, default icon, label) of a display; "Others" when unknown.
export const getVenueCategory = (display) => {
  const root = (display && display.open_ooh_venue_type_id_parent) || getVenuePath(display)[0];
  return CATEGORY_BY_ROOT[root] || OTHER_VENUE_CATEGORY;
};

// Most specific configured icon: walks the path from leaf to root (roots count
// too, since a category can group several), falling back to the category icon.
export const getVenueIcon = (display) => {
  const category = getVenueCategory(display);
  const subtypeIcons = category.subtypeIcons || {};
  const path = getVenuePath(display);
  for (let i = path.length - 1; i >= 0; i--) {
    if (subtypeIcons[path[i]]) return subtypeIcons[path[i]];
  }
  return category.icon;
};

// Most specific translated subtype name (level 3 ids are often missing from the
// translations, so it falls back to their parent), or the category name.
export const getVenueSubtypeLabel = (display, t) => {
  const path = getVenuePath(display);
  if (!path.length) return null;
  const id = [...path].reverse().find((venueId) => i18n.exists(`subtype_${venueId}`));
  return t(id ? `subtype_${id}` : getVenueCategory(display).labelKey);
};

const svgIcon = (width, height, body, anchorX, anchorY, pxPerUnit = 1) => ({
  url:
    "data:image/svg+xml;charset=UTF-8," +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${body}</svg>`
    ),
  size: new google.maps.Size(width * pxPerUnit, height * pxPerUnit),
  scaledSize: new google.maps.Size(width * pxPerUnit, height * pxPerUnit),
  anchor: new google.maps.Point(anchorX * pxPerUnit, anchorY * pxPerUnit),
});

// Looping "radar" pulse for screens on the cart: the shape grows and fades out.
// SVG animations only play because venue markers are not optimized (DisplayMarker).
const pulse = (shape, attrs, grow) =>
  `<${shape} ${attrs} fill="none" stroke-width="1.5">` +
  grow
    .map(([name, from, to]) => `<animate attributeName="${name}" values="${from};${to}" dur="1.6s" repeatCount="indefinite"/>`)
    .join("") +
  '<animate attributeName="stroke-opacity" values="0.9;0" dur="1.6s" repeatCount="indefinite"/>' +
  `</${shape}>`;

// Point marker: a colored dot on a 24px canvas, with room for the pulse around it.
const buildPointIcon = (color, isOnCart, isHovered) => {
  const radius = isHovered ? 8 : 6;
  const body =
    (isOnCart ? pulse("circle", `cx="12" cy="12" r="${radius}" stroke="${color}"`, [["r", radius, 11]]) : "") +
    `<circle cx="12" cy="12" r="${radius}" fill="${color}" stroke="${Colors.white}" stroke-width="1.5"/>`;
  return svgIcon(24, 24, body, 12, 12);
};

// Detail marker: a classic map drop, a circular head (center (18, 24.5), r 17)
// tapering to the tip (36 units = 34px wide). The pin sits DETAIL_MAX_LIFT units
// below the top of the canvas so it can rise on hover, and the anchor is the tip
// at rest (18, 52), on the screen location.
const DETAIL_PX_PER_UNIT = 34 / 36;
const DETAIL_MAX_LIFT = 6;
const DETAIL_LIFT_STEPS = 6;
const DETAIL_MARKER_PATH = "M18 52 C18 52 1 37 1 24.5 A17 17 0 0 1 35 24.5 C35 37 18 52 18 52 Z";
// Longest side of the icon, centered on the head. The pin icons leave some
// padding in their viewBox, so 23 still keeps every glyph inside the head.
const DETAIL_ICON_SIZE = 23;

// Under the pin: a pulsing colored mark when the screen is on the cart, or a
// shadow that grows as the pin rises.
const buildGround = (color, isOnCart, lift) => {
  if (isOnCart) {
    const ellipse = 'cx="18" cy="52" rx="9" ry="3.4"';
    return (
      pulse("ellipse", `${ellipse} stroke="${color}"`, [["rx", 9, 16], ["ry", 3.4, 6]]) +
      `<ellipse ${ellipse} fill="${color}" fill-opacity="0.85" stroke="${Colors.white}" stroke-width="1"/>`
    );
  }
  return lift ? `<ellipse cx="18" cy="52" rx="${4 + lift * 0.4}" ry="1.8" fill="#000" fill-opacity="${0.05 * lift}"/>` : "";
};

const buildDetailIcon = (color, icon, isOnCart, liftStep) => {
  const [width, height, , , svgPath] = icon.icon;
  const scale = DETAIL_ICON_SIZE / Math.max(width, height);
  const lift = (liftStep / DETAIL_LIFT_STEPS) * DETAIL_MAX_LIFT;
  const body =
    buildGround(color, isOnCart, lift) +
    `<g transform="translate(0 ${-lift})">` +
    `<path d="${DETAIL_MARKER_PATH}" fill="${color}" ` +
    `stroke="${Colors.white}" stroke-width="2" stroke-linejoin="round"/>` +
    `<path fill="${Colors.white}" d="${[].concat(svgPath).join(" ")}" ` +
    `transform="translate(${18 - (width * scale) / 2} ${24.5 - (height * scale) / 2}) scale(${scale})"/>` +
    "</g>";
  return svgIcon(36, 59, body, 18, 52, DETAIL_PX_PER_UNIT);
};

// Cached so PureComponent markers get the same icon reference across renders.
// Keyed by icon prefix + iconName, so custom icons need a unique iconName.
const markerIconCache = new Map();

// Map marker icon for a display: a colored dot in "point" mode (bigger on
// hover), a colored pin with the category/subtype icon in "detail" mode.
// lift (0..1) is how far a detail pin has risen; it defaults to fully risen on
// hover, and DisplayMarker passes the values in between while animating.
export const getVenueMarkerIcon = (display, { variant, isOnCart, isHovered, lift = isHovered ? 1 : 0 }) => {
  const { color } = getVenueCategory(display);
  const isPoint = variant === "point";
  const icon = isPoint ? null : getVenueIcon(display);
  const liftStep = Math.round(lift * DETAIL_LIFT_STEPS);
  const state = isPoint ? `point-${!!isHovered}` : `detail-${liftStep}`;
  const key = [color, icon && `${icon.prefix}-${icon.iconName}`, !!isOnCart, state].join("|");
  if (!markerIconCache.has(key)) {
    markerIconCache.set(
      key,
      isPoint ? buildPointIcon(color, isOnCart, isHovered) : buildDetailIcon(color, icon, isOnCart, liftStep)
    );
  }
  return markerIconCache.get(key);
};
